// Copyright 2026. WebPros International GmbH. All rights reserved.

/**
 * Legacy fallback for contexts without the async Clipboard API. Unlike @plesk/ui-library's
 * `copyToClipboard`, this reports whether the copy actually happened instead of discarding
 * `execCommand`'s result — callers of a one-time secret cannot afford to assume success.
 */
const copyViaHiddenTextarea = (value: string): boolean => {
    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();

    try {
        return document.execCommand('copy');
    } finally {
        document.body.removeChild(el);
    }
};

/**
 * Copy `value` to the clipboard, rejecting when the copy did not happen.
 *
 * The design system's helper selects a hidden textarea and calls the deprecated
 * `document.execCommand('copy')`, then throws its boolean result away. Callers therefore cannot
 * tell a successful copy from a failed one, which is how the API token dialog ended up claiming to
 * copy while it did not (SVM2-7352).
 *
 * The async Clipboard API needs no selection and reports failure, so prefer it. It is only
 * available in a secure context, which a self-hosted panel served over plain HTTP is not — hence
 * the fallback, which reports failure too.
 */
export const copyToClipboard = async (value: string): Promise<void> => {
    if (navigator.clipboard) {
        await navigator.clipboard.writeText(value);

        return;
    }

    if (!copyViaHiddenTextarea(value)) {
        throw new Error('Could not copy to the clipboard.');
    }
};
